import { Anchor, Button, Card, H2, H5 } from "@sellar-io/components";
import {
  Chart as ChartJS,
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Colors,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { SpinnerLegacy } from "../../components";
import { StatCard } from "../../components/card/StatCard";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import { useAnalytics } from "../../hooks/api/v2/analytics/useAnalytics";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { capitalize } from "@sellar-io/common";
import { Mixpanel } from "../../utils/mixpanel";
import { zLayer } from "@sellar-io/components";

ChartJS.register(
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Colors,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
);

type TimeframeType = {
  key: string;
  label: string;
  from: Date;
  to: Date;
};

const timeframes: TimeframeType[] = [
  {
    key: "thisWeek",
    label: "This week",
    from: dayjs().startOf("week").toDate(),
    to: dayjs().endOf("week").toDate(),
  },
  {
    key: "thisMonth",
    label: "This month",
    from: dayjs().startOf("month").toDate(),
    to: dayjs().endOf("month").toDate(),
  },
  {
    key: "thisYear",
    label: "This year",
    from: dayjs().startOf("year").toDate(),
    to: dayjs().endOf("year").toDate(),
  },
  {
    key: "lastWeek",
    label: "Last week",
    from: dayjs().startOf("week").subtract(1, "week").toDate(),
    to: dayjs().endOf("week").subtract(1, "week").toDate(),
  },
  {
    key: "lastMonth",
    label: "Last month",
    from: dayjs().startOf("month").subtract(1, "month").toDate(),
    to: dayjs().endOf("month").subtract(1, "month").toDate(),
  },
  {
    key: "lastYear",
    label: "Last year",
    from: dayjs().startOf("year").subtract(1, "year").toDate(),
    to: dayjs().endOf("year").subtract(1, "year").toDate(),
  },
  {
    key: "allTime",
    label: "All time",
    from: dayjs("2020-01-01").startOf("year").toDate(),
    to: dayjs().subtract(1, "month").endOf("month").toDate(),
  },
];

type TimeframeV2Type = {
  from: Date;
  to: Date;
};

const todayTimeframe: TimeframeV2Type = {
  from: dayjs().startOf("day").toDate(),
  to: dayjs().endOf("day").toDate(),
};

type TimeIntervalType = "today" | "month" | "year" | "allTime";

const timeInterval: TimeIntervalType[] = ["today", "month", "year", "allTime"];

export function Analytics(): ReactElement {
  return (
    <>
      <NavigationBarMenu title={"Analytics"} />
      <SpikeAnalytics />
    </>
  );
}

const SpikeAnalytics = () => {
  const analyticsV2Active = useRemoteFeatureFlags().isActive("analyticsV2");
  const analyticsAllTimeActive =
    useRemoteFeatureFlags().isActive("analyticsAllTime");

  const [timeframe, setTimeframe] = useState<TimeframeType["key"]>("lastMonth");
  const filteredTimeframe = timeframes.filter(
    (time) => time.key === timeframe,
  )[0];

  const [timeframeInterval, setTimeframeInterval] =
    useState<TimeIntervalType>("today");
  const [month, setMonth] = useState(dayjs().month());
  const [year, setYear] = useState(dayjs().year());

  const generateYears = () => {
    const limit = 2020;
    const current = dayjs().year();
    let index = current;
    let years = [];

    while (index >= limit) {
      years.push(index);
      index--;
    }

    return years;
  };

  const timeframeV2: TimeframeV2Type = (() => {
    const generateMonth = (): TimeframeV2Type => {
      return {
        from: dayjs().month(month).year(year).startOf("month").toDate(),
        to: dayjs().month(month).year(year).endOf("month").toDate(),
      };
    };

    const generateYear = (): TimeframeV2Type => {
      return {
        from: dayjs().year(year).startOf("year").toDate(),
        to: dayjs().year(year).endOf("year").toDate(),
      };
    };

    switch (timeframeInterval) {
      case "today":
        return todayTimeframe;

      case "month":
        return generateMonth();

      case "year":
        return generateYear();

      case "allTime":
        return {
          from: dayjs("2020-01-01").startOf("year").toDate(),
          to: dayjs().subtract(1, "month").endOf("month").toDate(),
        };

      default:
        return todayTimeframe;
    }
  })();

  const analyticsQuery = useAnalytics(
    {
      timeframe: {
        type: "timeframe",
        value: {
          from: analyticsV2Active
            ? timeframeV2.from
            : (filteredTimeframe?.from ??
              dayjs().startOf("month").subtract(1, "month").toDate()),
          to: analyticsV2Active
            ? timeframeV2.to
            : (filteredTimeframe?.to ??
              dayjs().endOf("month").subtract(1, "month").toDate()),
        },
        modifier: "BETWEEN",
      },
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  );
  const analyticsRefreshQuery = useAnalytics(
    {
      timeframe: {
        type: "timeframe",
        value: {
          from: analyticsV2Active
            ? timeframeV2.from
            : (filteredTimeframe?.from ??
              dayjs().startOf("month").subtract(1, "month").toDate()),
          to: analyticsV2Active
            ? timeframeV2.to
            : (filteredTimeframe?.to ??
              dayjs().endOf("month").subtract(1, "month").toDate()),
        },
        modifier: "BETWEEN",
      },
      refresh: true,
    },
    {
      enabled: false,
    },
  );
  const { isLoading, data } = analyticsQuery;
  const { data: refreshData, refetch, isSuccess } = analyticsRefreshQuery;

  const analyticsData = isSuccess ? refreshData : data;

  useEffect(() => {
    Mixpanel.track("Analytics Page Viewed");
  }, []);

  return (
    <div className="@container container mx-auto max-w-[1200px] bg-transparent px-4 pb-12">
      <div
        className={`sticky top-[53px] ${zLayer.pinned} flex w-full flex-wrap items-center justify-between gap-4 bg-neutral-100 py-2`}
      >
        <div className="flex flex-col gap-2 text-lg">
          <div className="flex items-center gap-2">
            <span className="font-semibold">Showing data</span>
            {analyticsData && analyticsData.dates.start && (
              <span>
                from {dayjs(analyticsData.dates.start).format("DD/MM/YYYY")}
              </span>
            )}
            {analyticsData && analyticsData.dates.end && (
              <span>
                to {dayjs(analyticsData.dates.end).format("DD/MM/YYYY")}
              </span>
            )}
          </div>
          {analyticsData && analyticsData.lastUpdated && (
            <div className="flex items-center gap-2 text-xs">
              <span>
                <span className="font-semibold">Last updated:</span>{" "}
                {dayjs(analyticsData.lastUpdated).format("HH:mm | DD/MM/YYYY")}
              </span>
              {dayjs().isAfter(
                dayjs(analyticsData.lastUpdated).add(1, "hour"),
              ) && (
                <Button
                  color="slim"
                  size="small"
                  className="py-1"
                  onClick={() => refetch()}
                >
                  Refresh
                </Button>
              )}
            </div>
          )}
          <div className="mt-2 self-start rounded-lg bg-purple-200 px-2 py-1">
            <p className="text-sm leading-none">
              All amounts <b>exclude VAT</b>
            </p>
          </div>
        </div>
        {analyticsV2Active ? (
          <div className="flex min-w-[165px] flex-col gap-2 self-start">
            <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
              <InputSelect
                defaultValue={timeframeInterval}
                onChange={(e) => {
                  setTimeframeInterval(e.target.value as TimeIntervalType);
                }}
                className="text-sm font-medium"
              >
                {timeInterval
                  .filter((timeframe) =>
                    analyticsAllTimeActive ? true : timeframe !== "allTime",
                  )
                  .map((timeframe, idx) => (
                    <InputOption key={idx} value={timeframe}>
                      {timeframe === "allTime"
                        ? "All time"
                        : capitalize(timeframe)}
                    </InputOption>
                  ))}
              </InputSelect>
            </InputContainer>
            <div className="flex justify-end gap-2">
              {timeframeInterval === "month" && (
                <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                  <InputSelect
                    defaultValue={month}
                    onChange={(e) => {
                      setMonth(parseInt(e.target.value));
                    }}
                    className="text-sm font-medium"
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
                      .filter((month) => {
                        if (year < dayjs().year()) return true;
                        return month <= dayjs().month();
                      })
                      .map((month, idx) => (
                        <InputOption key={idx} value={month}>
                          {dayjs().month(month).format("MMM")}
                        </InputOption>
                      ))}
                  </InputSelect>
                </InputContainer>
              )}
              {["month", "year"].includes(timeframeInterval) && (
                <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                  <InputSelect
                    defaultValue={year}
                    onChange={(e) => {
                      setYear(parseInt(e.target.value));
                    }}
                    className="text-sm font-medium"
                  >
                    {generateYears().map((year, idx) => (
                      <InputOption key={idx} value={year}>
                        {year}
                      </InputOption>
                    ))}
                  </InputSelect>
                </InputContainer>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="mr-auto inline-block text-sm">
              Displaying data
            </span>
            <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
              <InputSelect
                defaultValue={"lastMonth"}
                onChange={(e) => {
                  setTimeframe(e.target.value as TimeframeType["key"]);
                }}
                className="text-sm font-medium"
              >
                {timeframes
                  .filter((timeframe) =>
                    analyticsAllTimeActive ? true : timeframe.key !== "allTime",
                  )
                  .map((time, idx) => (
                    <InputOption key={idx} value={time.key}>
                      {time.label}
                    </InputOption>
                  ))}
              </InputSelect>
            </InputContainer>
          </div>
        )}
      </div>
      {isLoading ? (
        <SpinnerLegacy />
      ) : (
        <div className="grid grid-cols-1 gap-2 pt-4 lg:pt-0 @lg:grid-cols-2 @3xl:grid-cols-3">
          {analyticsData &&
            analyticsData.components.map((component, idx) => {
              switch (component.type) {
                case "stat":
                  return (
                    <StatCard
                      key={idx}
                      title={component.title}
                      stat={component.stat}
                      subtitle={component.subtitle.replace(
                        "%timeframe%",
                        filteredTimeframe?.label ?? "",
                      )}
                      className={twMerge(
                        "bg-white @3xl:col-span-1",
                        component.customClass,
                      )}
                    />
                  );
                case "title":
                  return (
                    <H2
                      key={idx}
                      className={twMerge(
                        "w-full py-2 @lg:col-span-2 @3xl:col-span-3",
                        component.customClass,
                      )}
                    >
                      {component.title}
                    </H2>
                  );
                case "textBlock":
                  return (
                    <Card
                      key={idx}
                      className={twMerge(
                        "bg-white",
                        "border border-zinc-300 p-4",
                        component.customClass,
                      )}
                    >
                      <H2>{component.title}</H2>
                      <p>{component.text}</p>
                    </Card>
                  );
                case "barchart":
                  return (
                    <BarChart
                      key={idx}
                      title={component.title}
                      labels={component.labels ?? []}
                      datasets={component.datasets}
                      className={component.customClass}
                    />
                  );
                case "chart":
                  return (
                    <Chart
                      key={idx}
                      type={component.chartType}
                      title={component.title}
                      label={component.label}
                      data={component.data}
                      className={component.customClass}
                    />
                  );
                case "table":
                  return (
                    <Table
                      key={idx}
                      title={component.title}
                      data={component.data}
                      firstRowAsHeader={component.firstRowAsHeader}
                      className={component.customClass}
                    />
                  );
              }
            })}
        </div>
      )}
    </div>
  );
};

const BarChart = ({
  title,
  labels,
  datasets,
  className,
}: {
  title: string;
  labels: string[];
  datasets: {
    label: string;
    backgroundColor: "red" | "blue" | "yellow" | "green";
    data: number[];
  }[];
  className?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<ChartJS<"bar"> | null>(null);

  const renderChart = useCallback(() => {
    if (!canvasRef.current) return;

    chartRef.current = new ChartJS(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets,
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          ...(title && {
            title: {
              display: true,
              text: title,
            },
          }),
        },
        scales: {
          y: {
            title: {
              display: true,
              text: "Count",
            },
          },
          y1: {
            type: "linear",
            display: true,
            position: "right",
            title: {
              display: true,
              text: "£",
            },
          },
        },
      },
    });
  }, [datasets, labels, title]);

  const destroyChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  }, []);

  useEffect(() => {
    renderChart();

    return () => destroyChart();
  }, [destroyChart, renderChart]);

  return (
    <div
      className={twMerge(
        "relative flex h-[400px] w-full justify-center rounded-lg border border-zinc-300 bg-white p-4",
        className,
      )}
    >
      <canvas ref={canvasRef} role="img" />
    </div>
  );
};

const Chart = ({
  type,
  title,
  label,
  data,
  className,
}: {
  type: "doughnut" | "line";
  title: string;
  label: string;
  data: { x: string; y: number }[];
  className?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<ChartJS<
    "doughnut" | "line",
    number[],
    string
  > | null>(null);

  const renderChart = useCallback(() => {
    if (!canvasRef.current) return;

    chartRef.current = new ChartJS(canvasRef.current, {
      type,
      data: {
        labels: data.map((row) => row.x),
        datasets: [
          {
            label,
            data: data.map((row) => row.y),
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          ...(title && {
            title: {
              display: true,
              text: title,
            },
          }),
        },
      },
    });
  }, [data, label, title, type]);

  const destroyChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  }, []);

  useEffect(() => {
    renderChart();

    return () => destroyChart();
  }, [destroyChart, renderChart]);

  return (
    <div
      className={twMerge(
        "relative flex h-[400px] w-full justify-center rounded-lg border border-zinc-300 bg-white p-4",
        className,
      )}
    >
      <canvas ref={canvasRef} role="img" />
    </div>
  );
};

const Table = ({
  data,
  title,
  firstRowAsHeader = true,
  className,
}: {
  data: { text: string; link?: string }[][];
  title: string;
  firstRowAsHeader?: boolean;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "relative flex w-full flex-col gap-4 rounded-lg border border-zinc-300 bg-white p-4 text-left",
        className,
      )}
    >
      <H5>{title}</H5>
      <div className="overflow-x-auto">
        <table className="w-full table-auto border-separate border-spacing-0 rounded-lg">
          {firstRowAsHeader && (
            <thead className="text-base font-semibold">
              <tr className="bg-zinc-50">
                {data[0]?.map((header, idx) => (
                  <th
                    key={idx}
                    className={twMerge(
                      "min-w-[150px] border border-zinc-300 px-2 py-1",
                      idx === 0 && "sticky left-0 z-10 bg-zinc-50",
                    )}
                  >
                    {header.text}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {data
              .filter((_, idx) => idx !== 0 || !firstRowAsHeader)
              .map((row, idx) => (
                <tr key={idx}>
                  {row.map((cell, idx) => (
                    <td
                      key={idx}
                      className={twMerge(
                        "min-w-[150px] border border-zinc-300 px-2 py-1",
                        idx === 0 && "sticky left-0 z-10 bg-white",
                      )}
                    >
                      <TableCell cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TableCell = ({ cell }: { cell: { text: string; link?: string } }) => {
  if (cell.link)
    return (
      <Anchor href={cell.link} className="hover:font-semibold hover:underline">
        {cell.text}
      </Anchor>
    );
  return cell.text;
};
